import { Alert, AlertTitle, Box, Button, Divider, Grid, Stack, Typography, styled } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { Flex } from "@zfp-akademie-a-s/zfp-design";
import { FormSelect, FormSwitch } from "@zfp-akademie-a-s/zfp-design/form";
import { FormNumberField } from "@zfp-akademie-a-s/zfp-design/form/number-field";
import { RestoreIcon } from "Components/_Shared/Icons/Icons";
import type { RiskInsuranceParametersFormType } from "Forms/Schemas/Insurance/riskInsuranceParametersFormSchema";
import { type Option, toSelectOptions } from "Forms/fieldOptions";
import {
  getGetInsuranceRiskParametersQueryKey,
  getListInsuranceRisksQueryKey,
  useCreateInsuranceRisk,
  useGetInsuranceRiskParameters,
  useGetSocialSupport,
  useListInsuranceFamilyLiabilities,
  useListInsuranceRisks,
  useListLiabilityItems,
  useRecalculateAllInsuranceRisks,
  useUpdateInsuranceRiskParameters,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import {
  AmountFrequency,
  type CoverageParametersBody,
  CoverageParametersCoverageAmountType,
  type CreateRiskBodyRiskType,
  RiskRiskType,
} from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { COVERAGE_AMOUNT_TYPE_RESOURCE_KEY } from "Infrastructure/Utils/InsuranceUtils";
import { type FC, useContext, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { AnalysisDetailContext } from "../Detail/AnalysisDetailContext";
import { Risk } from "./Risk";
import { RiskInsuranceAdd } from "./RiskInsuranceAdd";
import { RiskInsuranceEmptyState } from "./RiskInsuranceEmptyState";

type Props = {
  readonly clientId: string;
  readonly analysisId: string;
  readonly versionId: string;
  readonly isAdult: boolean;
};

export const RiskInsurance: FC<Props> = ({ clientId, analysisId, versionId, isAdult }) => {
  const queryClient = useQueryClient();

  const { readOnly } = useContext(AnalysisDetailContext);
  const { t } = useResource();

  const { data: stateAidData } = useGetSocialSupport(analysisId, versionId, clientId, { query: { enabled: !!clientId } });
  const { data: insuranceRisks } = useListInsuranceRisks(analysisId, versionId, clientId);
  const { data: insuranceRiskParameters } = useGetInsuranceRiskParameters(analysisId, versionId, clientId);
  const { data: familyLiabilities } = useListInsuranceFamilyLiabilities(analysisId, versionId, { query: { staleTime: 10 * 60 * 1000 } });
  // The insurance family-liabilities overview doesn't carry dueDate — cross-reference the full liability items.
  const { data: liabilityItems } = useListLiabilityItems(analysisId, versionId, { query: { staleTime: 10 * 60 * 1000 } });
  const liabilityItemsByGuid = new Map(liabilityItems?.map((item) => [item.guid, item]));

  const { mutateAsync: createInsuranceRisk } = useCreateInsuranceRisk({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListInsuranceRisksQueryKey(analysisId, versionId, clientId) });
        toast.success("Riziko úspěšně přidáno.");
      },
    },
  });

  const { mutateAsync: updateInsuranceRiskParameters } = useUpdateInsuranceRiskParameters({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getGetInsuranceRiskParametersQueryKey(analysisId, versionId, clientId) });
      },
    },
  });

  const { mutateAsync: recalculateInsuranceRisks } = useRecalculateAllInsuranceRisks({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListInsuranceRisksQueryKey(analysisId, versionId, clientId) });
      },
    },
  });

  const defaultValues: RiskInsuranceParametersFormType = {
    interestRate: insuranceRiskParameters?.interestRate ?? ("0" as any),
    coverageAmountType: insuranceRiskParameters
      ? insuranceRiskParameters.coverageAmountType
      : CoverageParametersCoverageAmountType.client_incomes,
    coverageAmount: insuranceRiskParameters?.coverageAmount.value ?? ("0" as any),
    liquidityReserve: insuranceRiskParameters?.liquidityReserve.value ?? ("0" as any),
    isRecommendedCalculationPreferred: !!insuranceRiskParameters?.isRecommendedCalculationPreferred,
  };

  const hasInsuranceRisks = insuranceRisks && insuranceRisks.length > 0;

  const coverageAmountTypes: Option[] = Object.values(CoverageParametersCoverageAmountType).map((value) => ({
    value,
    name: t(Resources.AnalysisDetail.Insurance.CoverageAmountType[COVERAGE_AMOUNT_TYPE_RESOURCE_KEY[value]]),
  }));

  const riskDropdownItems: RiskRiskType[] = Object.values(RiskRiskType).filter((riskType) => {
    if (insuranceRisks) {
      return !insuranceRisks.map((i) => i.riskType).includes(riskType);
    }

    return true;
  });

  const { reset, handleSubmit, control, watch } = useForm<RiskInsuranceParametersFormType>();

  // biome-ignore lint: <Open is required.>
  useEffect(() => {
    reset(defaultValues);
  }, [insuranceRiskParameters, reset]);

  const handleClickAddRisk = async (value: RiskRiskType) => {
    try {
      await createInsuranceRisk({
        analysisId,
        versionId,
        clientGuid: clientId,
        data: { riskType: value as CreateRiskBodyRiskType },
      });
    } catch {
      toast.error(t(Resources.Common.Error));
    }
  };

  const onSubmit = async (data: RiskInsuranceParametersFormType, updates?: Partial<CoverageParametersBody>) => {
    try {
      await updateInsuranceRiskParameters({
        analysisId,
        versionId,
        clientGuid: clientId,
        data: {
          coverageAmountType: data.coverageAmountType as CoverageParametersCoverageAmountType,
          isRecommendedCalculationPreferred: data.isRecommendedCalculationPreferred,
          interestRate: data.interestRate,
          liquidityReserve: {
            value: data.liquidityReserve,
            currencyCode: "CZK",
            frequency: AmountFrequency.one_time,
          },
          coverageAmount: {
            value: data.coverageAmount,
            currencyCode: "CZK",
            frequency: AmountFrequency.one_time,
          },
          ...updates,
        },
      });
    } catch {
      toast.error(t(Resources.Common.Error));
    }
  };

  const onRecalculate = async () => {
    try {
      await recalculateInsuranceRisks({ analysisId, versionId, clientGuid: clientId });
    } catch {
      toast.error(t(Resources.Common.Error));
    }
  };

  const coverageAmountType = watch("coverageAmountType");
  const showStateAidWarning =
    stateAidData?.firstDisability.value !== 0 ||
    stateAidData?.secondDisability.value !== 0 ||
    stateAidData?.thirdDisability.value !== 0 ||
    stateAidData?.orphanPension.value !== 0 ||
    stateAidData?.widowPension.value !== 0;
  const missingStateAid = !showStateAidWarning;
  const missingDueDate = !familyLiabilities?.every((item) => !!liabilityItemsByGuid.get(item.liability.guid)?.dueDate);

  return (
    <Stack spacing={Spacing.XL}>
      <Box display="flex" justifyContent="space-between">
        <Typography variant="subtitle1" fontWeight={600}>
          {t(Resources.AnalysisDetail.Insurance.RiskInsurance)}
        </Typography>

        <Button variant="text" disabled={readOnly} sx={{ width: "fit-content" }} startIcon={<RestoreIcon />} onClick={onRecalculate}>
          {t(Resources.AnalysisDetail.Insurance.RecalculateInsuranceAmounts)}
        </Button>
      </Box>
      <Grid container spacing={Spacing.XL}>
        <Grid size={3}>
          <FormNumberField
            control={control}
            name="interestRate"
            label={t(Resources.AnalysisDetail.Insurance.InvestmentProductInterest)}
            scale={2}
            min={0.1}
            thousandsSeparator=" "
            unit="% p.a."
            onBlur={() => handleSubmit((data) => onSubmit(data))()}
            fullWidth
          />
        </Grid>
        <Grid size={3}>
          <FormSelect
            control={control}
            name="coverageAmountType"
            label={t(Resources.AnalysisDetail.Insurance.CoverageType)}
            options={toSelectOptions(coverageAmountTypes)}
            placeholder={t(Resources.Common.SelectPlaceholder)}
            disabled={readOnly}
            onChange={() => handleSubmit((data) => onSubmit(data))()}
            fullWidth
          />
        </Grid>
        <Grid size={3}>
          <FormNumberField
            control={control}
            name="coverageAmount"
            label={t(Resources.AnalysisDetail.Insurance.CoverageAmount)}
            scale={2}
            thousandsSeparator=" "
            unit={`${t(Resources.Common.CZKSuffix)}/${t(Resources.Common.Month)}`}
            disabled={coverageAmountType !== CoverageParametersCoverageAmountType.other}
            onBlur={() => handleSubmit((data) => onSubmit(data))()}
            fullWidth
          />
        </Grid>
        <Grid size={3}>
          <FormNumberField
            control={control}
            name="liquidityReserve"
            label={t(Resources.AnalysisDetail.Insurance.LiquidReserve)}
            scale={2}
            thousandsSeparator=" "
            unit={t(Resources.Common.CZKSuffix)}
            onBlur={() => handleSubmit((data) => onSubmit(data))()}
            fullWidth
          />
        </Grid>
        <Grid size={12}>
          <FormSwitch
            disabled={readOnly}
            control={control}
            name="isRecommendedCalculationPreferred"
            label={t(Resources.AnalysisDetail.Insurance.ClientPrefersDefaultAmounts)}
            onChange={() =>
              handleSubmit((data) =>
                onSubmit(data, {
                  isRecommendedCalculationPreferred: !insuranceRiskParameters?.isRecommendedCalculationPreferred,
                }),
              )()
            }
          />
        </Grid>
        <Grid size={12}>
          {isAdult && (missingStateAid || missingDueDate) && (
            <Alert severity="warning">
              <AlertTitle>Varování</AlertTitle>
              {missingStateAid && <div>Vyplňte hodnoty státní podpory.</div>}
              {missingDueDate && <div>U nesplaceného úvěru klienta není uvedena splatnost.</div>}
            </Alert>
          )}
        </Grid>
      </Grid>
      <Divider />
      {hasInsuranceRisks && (
        <Flex justifyContent={"flex-end"}>
          <RiskInsuranceAdd items={riskDropdownItems} onClick={handleClickAddRisk} />
        </Flex>
      )}
      {!hasInsuranceRisks && (
        <Flex justifyContent={"center"} alignItems={"center"} gap={Spacing.XXL}>
          <RiskInsuranceEmptyState onClick={onRecalculate} />
          <RiskInsuranceAdd items={riskDropdownItems} onClick={handleClickAddRisk} />
        </Flex>
      )}
      {insuranceRisks?.map((risk) => (
        <Risk key={risk.guid} risk={risk} analysisId={analysisId} versionId={versionId} clientId={clientId} />
      ))}
      {hasInsuranceRisks && (
        <>
          <Divider />
          <Flex justifyContent={"flex-end"}>
            <RiskInsuranceAdd items={riskDropdownItems} onClick={handleClickAddRisk} />
          </Flex>
        </>
      )}
    </Stack>
  );
};
